import angular from 'angular'

import setCss from '../utils/set-css'
import envRaw from '../utils/env-raw'
import StrictObj from '../utils/strict-obj'
import embedResize from '../utils/embed-resize'

import { wrap, isBoolean } from 'lodash-es'
import { offEvent, onEventPassive } from '../utils/on-event'
import { makeOR, makeAND, defaultTo } from '../utils/bits'

angular
  .module('ggs.gallery')
  .factory('EmbedManager', EmbedManagerProvider)

function EmbedManagerProvider(
  qh,
  env,
  rootResize,
  $rootElement,
  DefineMethod,
  DefineHandler) {

  const CLOSE_MODALS_RE = /CloseGalleryModals/
  const OPEN_SAMPLE_MODAL_RE = /OpenSampleGalleryModal/
  const SHOW_PREVIEWS_DESC_RE = /ShowGalleryPreviewsDesc/
  const HIDE_PREVIEWS_DESC_RE = /HideGalleryPreviewsDesc/

  const READY_SIGN = 'GalleryReady'
  const RESIZE_SIGN = 'GalleryResize'
  const LOADED_SIGN = 'GalleryLoaded'
  const VOTE_UP_SIGN = 'GalleryVoteUp'
  const CAROUSEL_SIGN = 'GalleryCarousel'
  const BACKGROUND_SIGN = 'GalleryBackground'

  return function EmbedManager(
    properties,
    extFlow,
    postVoteEmitter,
    openSamplePost,
    closeModals,
    headerHeightGetter,
    headerHeightEmitter,
    readyStateManager) {

    const sign = 'galleryMain::embedManager'
    const manager = StrictObj({
      wrapM: DefineMethod(sign),
      wrapH: DefineHandler(sign)
    })

    manager.wrapM('init', () => {
      // messageHandler
      onEventPassive(window, 'message', manager.messageHandler)
      // voteHandler
      postVoteEmitter.on(manager.voteHandler)
      // innerHeightHandler
      rootResize.height.on(manager.innerHeightHandler)
      // outerWidthHandler
      embedResize.containerWidth.on(manager.outerWidthHandler)
      // backgroundHandler
      headerHeightEmitter.on(manager.backgroundHandler)
      properties.key2emitter.body_background_color.on(manager.backgroundHandler)
      properties.key2emitter.body_background_image.on(manager.backgroundHandler)
      properties.key2emitter.body_bg_color_opacity.on(manager.backgroundHandler)
      properties.key2emitter.body_bg_image_stretch.on(manager.backgroundHandler)
      properties.key2emitter.body_bg_apply_to_root.on(manager.backgroundHandler)
      // carouselHandler
      properties.key2emitter.carousel_mode.on(manager.carouselHandler)
      // readyHandler
      readyStateManager.readyEmitter.on(manager.readyHandler)
      // loadedHandler
      readyStateManager.loadedEmitter.on(manager.loadedHandler)
      // other
      return qh.join(
        manager.processCarousel(),
        manager.processBackground(),
        manager.processOuterWidth())
    })

    manager.wrapH('destroy', () => {
      // messageHandler
      offEvent(window, 'message', manager.messageHandler)
      // voteHandler
      postVoteEmitter.off(manager.voteHandler)
      // innerHeightHandler
      rootResize.height.off(manager.innerHeightHandler)
      // outerWidthHandler
      embedResize.containerWidth.off(manager.outerWidthHandler)
      // backgroundHandler
      headerHeightEmitter.off(manager.backgroundHandler)
      properties.key2emitter.body_background_color.off(manager.backgroundHandler)
      properties.key2emitter.body_background_image.off(manager.backgroundHandler)
      properties.key2emitter.body_bg_color_opacity.off(manager.backgroundHandler)
      properties.key2emitter.body_bg_image_stretch.off(manager.backgroundHandler)
      properties.key2emitter.body_bg_apply_to_root.off(manager.backgroundHandler)
      // carouselHandler
      properties.key2emitter.carousel_mode.off(manager.carouselHandler)
      // readyHandler
      readyStateManager.readyEmitter.off(manager.readyHandler)
      // loadedHandler
      readyStateManager.loadedEmitter.off(manager.loadedHandler)
    })

    manager.wrapM('send', (...args) => {
      args = args.map((msg) => isBoolean(msg) ? Number(msg) : msg)
      window.parent.postMessage(properties.key + args.join(''), '*')
    })

    // messageHandler

    manager.wrapM('openSampleModal',
      qh.makeIf(
        envRaw.getters.liveEditMode,
        openSamplePost))

    manager.wrapM('closeModals',
      qh.makeIf(
        envRaw.getters.liveEditMode,
        closeModals))

    manager.wrapM('showPreviewsDesc',
      qh.makeIf(
        envRaw.getters.liveEditMode,
        extFlow.showPreviewsDesc))

    manager.wrapM('hidePreviewsDesc',
      qh.makeIf(
        envRaw.getters.liveEditMode,
        extFlow.hidePreviewsDesc))

    manager.wrapH('messageHandler',
      qh.makeJoin(
        qh.makeIf(makeEventTest(CLOSE_MODALS_RE), manager.closeModals),
        qh.makeIf(makeEventTest(OPEN_SAMPLE_MODAL_RE), manager.openSampleModal),
        qh.makeIf(makeEventTest(SHOW_PREVIEWS_DESC_RE), manager.showPreviewsDesc),
        qh.makeIf(makeEventTest(HIDE_PREVIEWS_DESC_RE), manager.hidePreviewsDesc)))

    // voteHandler

    manager.wrapM('sendVoteUp', (_post, isVoted, _votesCount) => {
      if (isVoted) {
        return manager.send(VOTE_UP_SIGN)
      }
    })

    manager.wrapM('processVote',
      qh.makeIf(
        makeAND(
          env.getters.isIframeMode,
          properties.key2get.capture_integration),
        manager.sendVoteUp))

    manager.wrapH('voteHandler',
      manager.processVote)

    // innerHeightHandler

    manager.wrapM('sendHeight',
      qh.makeFlow(
        rootResize.getHeight,
        wrap(RESIZE_SIGN, manager.send)))

    manager.wrapM('processInnerHeight',
      qh.makeIf(
        env.getters.isIframeMode,
        qh.makeOver(
          manager.sendHeight,
          qh.makeDelay(200),
          manager.sendHeight)))

    manager.wrapH('innerHeightHandler',
      manager.processInnerHeight)

    // outerWidthHandler

    manager.wrapM('applyWidth', () => {
      setCss($rootElement, {
        width: embedResize.getContainerWidth(),
        maxWidth: '100%'
      })
    })

    manager.wrapM('processOuterWidth',
      qh.makeIf(
        makeAND(
          makeOR(
            env.getters.isIframeMode,
            envRaw.getters.liveEditMode),
          embedResize.isContainerReady),
        manager.applyWidth))

    manager.wrapH('outerWidthHandler',
      manager.processOuterWidth)

    // backgroundHandler

    manager.wrapM('sendBackground', () => {
      return manager.send(BACKGROUND_SIGN, JSON.stringify({
        header_height: defaultTo(headerHeightGetter(), 0),
        body_background_color: properties.body_background_color,
        body_background_image: properties.body_background_image,
        body_bg_color_opacity: properties.body_bg_color_opacity,
        body_bg_image_stretch: properties.body_bg_image_stretch,
        body_bg_apply_to_root: properties.body_bg_apply_to_root
      }))
    })

    manager.wrapM('processBackground',
      qh.makeIf(
        env.getters.isIframeMode,
        manager.sendBackground))

    manager.wrapH('backgroundHandler',
      manager.processBackground)

    // carouselHandler

    manager.wrapM('sendCarousel',
      qh.makeFlow(
        properties.key2get.carousel_mode,
        wrap(CAROUSEL_SIGN, manager.send)))

    manager.wrapM('processCarousel',
      qh.makeIf(
        env.getters.isIframeMode,
        manager.sendCarousel))

    manager.wrapH('carouselHandler',
      manager.processCarousel)

    // readyHandler

    manager.wrapM('processReady',
      wrap(READY_SIGN, manager.send))

    manager.wrapH('readyHandler',
      manager.processReady)

    // loadedHandler

    manager.wrapM('processLoaded',
      wrap(LOADED_SIGN, manager.send))

    manager.wrapH('loadedHandler',
      manager.processLoaded)

    return manager

    // other

    function makeEventTest(testRE) {
      return function makeEventTest_wrapper(event) {
        return testRE.test(event.data)
      }
    }
  }
}
